import { Divider, LinearProgress } from '@mui/material';
import { AgGridReact } from 'ag-grid-react';
import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Header from '../../components/layout/Header';
import PrimaryBox from '../../components/layout/PrimaryBox';
import CreateActivityNeedsModal from '../../components/modals/operations/needs/CreateActivityNeedsModal';
import ImportLastNeedsModal from '../../components/modals/operations/needs/ImportLastNeedsModal';
import NeedsGlobalTable from '../../components/tables/operations/needs/NeedsGlobalTable';
import IconButton from '../../components/ui/IconButton';
import { activityStateEnum, localStorageKeys, permissionLevelEnum, reportStateEnum, stepModeEnum } from '../../constants';
import { getActivityStateColor } from '../../constants/colors';
import { useImportActivitiesBySiteMutation } from '../../hooks/mutations/useActivityMutation';
import { useImportNeedsMutation } from '../../hooks/mutations/usePlanningMutation';
import { useActivitiesQuery } from '../../hooks/queries/useActivitiesQuery';
import { useActivitiesTypesQuery } from '../../hooks/queries/useActivitiesTypesQuery';
import { useTrucksQuery } from '../../hooks/queries/useTrucksQuery';
import { useUsersQuery } from '../../hooks/queries/useUsersQuery';
import { useAuth } from '../../hooks/useAuth';
import { useBoolean } from '../../hooks/useBoolean';
import { usePageTitle } from '../../hooks/usePageTitle';
import { DataItem } from '../../services/apiSLTP/dailyReports/activities';
import { Site } from '../../services/apiSLTP/sites/sites';
import { capitalizeFirstLetter, formatName } from '../../utils/capitalize';
import { getTomorrowDate } from '../../utils/dates';
import { getItemFromLocalStorage, storeItemToLocalStorage } from '../../utils/localStorage';

const NeedsPage: React.FC = () => {
    const [currentDate, setCurrentDate] = useState(() => {
        return new Date(getItemFromLocalStorage(localStorageKeys.DATE) || getTomorrowDate());
    });

    const [showCalendarModal, setShowCalendarModal] = useState(false);
    const [selectedTab, setSelectedTab] = useState(parseInt(getItemFromLocalStorage(localStorageKeys.TAB.NEEDS) || '0'));
    const [hoveredConducId, setHoveredConducId] = useState<string | null>(null);
    const [selectedFilterConducIds, setSelectedFilterConducIds] = useState<string[]>([]);
    const siteRefs = useRef<Record<string, HTMLDivElement | null>>({});
    const aggridRefs = useRef<Record<string, AgGridReact | null>>({});

    const { user } = useAuth();
    const { t } = useTranslation();
    usePageTitle(capitalizeFirstLetter(t('needs')));

    const importLastNeedsModal = useBoolean(false);
    const createActivityNeedsModal = useBoolean(false);

    const {
        data: activities,
        isLoading,
        refetch,
    } = useActivitiesQuery({
        queries: {
            startDate: currentDate.toISOString(),
            endDate: currentDate.toISOString(),
            stepMode: stepModeEnum.NEEDS,
        },
    });

    const { data: dataTrucks, isLoading: trucksIsLoading, isError: trucksIsError, isSuccess: trucksIsSuccess } = useTrucksQuery({});
    const { data: dataUsers, isLoading: isUsersLoading } = useUsersQuery({ queries: { active: true } });
    const { data: dataActivitiesTypes, isLoading: isActivitiesTypesLoading } = useActivitiesTypesQuery({ queries: { active: true } });

    const { mutate: mutateImportNeeds, isLoading: isImportNeedsLoading } = useImportNeedsMutation({ onSettled: () => refetch() });
    const { mutate: submitNeeds, isLoading: isSubmitNeedsLoading } = useImportActivitiesBySiteMutation({ onSettled: () => refetch() });

    const handleDateChange = (newDate: string) => {
        setCurrentDate(new Date(newDate));
        storeItemToLocalStorage(localStorageKeys.DATE, newDate);
    };

    const setSpecificHour = () => {
        const date = new Date(currentDate);
        date.setHours(7, 0);
        return date.toISOString();
    };

    const checkSiteState = (siteId: string): number => {
        let tmpState = 0;
        const tmpActivities = activities?.filter((value) => value.siteId._id === siteId);
        if (!tmpActivities || tmpActivities.length === 0) return activityStateEnum.NEEDS;
        for (let i = 0; i < tmpActivities.length; i++) {
            if (tmpActivities[i].activityState > tmpState) tmpState = tmpActivities[i].activityState;
        }
        return tmpState;
    };

    const sortedActivities = (siteId: string) => {
        return (activities || [])
            .filter((activity) => activity.siteId._id === siteId)
            .sort((a, b) => {
                if (a.isTruckManager) return -1;
                if (b.isTruckManager) return 1;

                if (a.reportId.userId.profileId.permsLevel < b.reportId.userId.profileId.permsLevel) return -1;
                if (a.reportId.userId.profileId.permsLevel > b.reportId.userId.profileId.permsLevel) return 1;

                return new Date(a.startTime).getTime() - new Date(b.startTime).getTime();
            });
    };

    const getUniqueSites = (dataItems: DataItem[]): Site[] => {
        const uniqueSitesMap = new Map<string, Site>();

        for (const item of dataItems.filter((activity) => {
            if (!activity.siteId.conducId?._id) return true;
            if (selectedFilterConducIds.length === 0) return true;
            return !selectedFilterConducIds.includes(activity.siteId.conducId?._id);
        })) {
            const site = item.siteId;
            if (site && !uniqueSitesMap.has(site._id)) {
                uniqueSitesMap.set(site._id, site);
            }
        }

        return Array.from(uniqueSitesMap.values()).sort((a, b) => {
            if (a.conducId?._id === user?._id && b.conducId?._id !== user?._id) return -1;
            if (b.conducId?._id === user?._id) return 1;

            const aConducName = formatName(a.conducId?.firstName, a.conducId?.lastName);
            const bConducName = formatName(b.conducId?.firstName, b.conducId?.lastName);

            if (aConducName < bConducName) return -1;
            if (aConducName > bConducName) return 1;

            return a.name.localeCompare(b.name);
        });
    };

    const checkEditableRow = (activity: DataItem) => {
        if (user && user?.profileId.permsLevel > permissionLevelEnum.CONDUC) return false;
        if (activity.activityState <= activityStateEnum.NEEDSFILLED) return false;
        if (activity.siteId.conducId?._id !== user?._id && activity.siteId.assistantConducId?._id !== user?._id) return false;
        return true;
    };

    interface ConducTotal {
        conducId: string;
        designation: string;
        color?: string;
        totalActivities: number;
        totalSent: number;
        totalImported: number;
    }

    const getConducteursTotalPrice = (): ConducTotal[] => {
        const map = new Map<string, ConducTotal>();

        activities
            ?.sort((a, b) => {
                if (a.siteId.conducId?._id === user?._id && b.siteId.conducId?._id !== user?._id) return -1;
                if (b.siteId.conducId?._id === user?._id) return 1;

                const aConducName = formatName(a.siteId.conducId?.firstName, a.siteId.conducId?.lastName);
                const bConducName = formatName(b.siteId.conducId?.firstName, b.siteId.conducId?.lastName);

                if (aConducName < bConducName) return -1;
                if (aConducName > bConducName) return 1;

                return a.siteId.name.localeCompare(b.siteId.name);
            })
            .forEach((activity) => {
                const conduc = activity.siteId.conducId;
                if (!conduc || !conduc._id) return;

                const conducId = conduc._id;
                const designation = formatName(conduc.firstName, conduc.lastName) || t('unknown');
                const color = conduc.color;

                const existing = map.get(conducId);

                if (existing) {
                    existing.totalActivities += 1;
                    existing.totalSent += activity?.activityState === activityStateEnum.NEEDSSENT ? 1 : 0;
                    existing.totalImported += activity?.activityState === activityStateEnum.NEEDSIMPORTED ? 1 : 0;
                } else {
                    map.set(conducId, {
                        conducId,
                        designation,
                        totalSent: activity?.activityState === activityStateEnum.NEEDSSENT ? 1 : 0,
                        totalImported: activity?.activityState === activityStateEnum.NEEDSIMPORTED ? 1 : 0,
                        totalActivities: 1,
                        color,
                    });
                }
            });

        return Array.from(map.values());
    };

    return (
        <PrimaryBox>
            <Header
                title={t('operations')}
                breadcrumbs={[{ label: capitalizeFirstLetter(t('needs')) }]}
                subTitle={capitalizeFirstLetter(t('needs'))}
                initialDate={currentDate}
                onDateChange={(newDate) => handleDateChange(newDate.toISOString())}
            >
                <Divider orientation="vertical" flexItem variant="middle" />
                <IconButton
                    iconName="verticalAlignBottom"
                    onClick={() => {
                        importLastNeedsModal.setTrue();
                    }}
                    title={capitalizeFirstLetter(t('importLastNeeds'))}
                />
                <IconButton
                    iconName="addCircle"
                    onClick={() => {
                        createActivityNeedsModal.setTrue();
                    }}
                    title={capitalizeFirstLetter(t('addActivity'))}
                />
                {!!user && user?.profileId.permsLevel <= permissionLevelEnum.PLANNING && (
                    <>
                        <IconButton
                            iconName="verticalAlignTop"
                            onClick={() => mutateImportNeeds({ date: currentDate.toISOString() }, { onSettled: () => refetch() })}
                            title={capitalizeFirstLetter(t('exportNeeds'))}
                            loading={isImportNeedsLoading}
                        />
                    </>
                )}
            </Header>

            {/*<CustomTabs selectedTab={selectedTab} handleTabChange={handleTabChange} tabLabels={[t('overall')]} />*/}

            {selectedTab === 0 && (
                <>
                    <div className=" border-radius-10px fx-c pad-10px gap-10px">
                        {!!activities?.length && (
                            <>
                                <div className="fx-c gap-5px">
                                    <div className="w-100 h-30px border-radius-10px of-y-hidden fx-r gap-5px ">
                                        {(() => {
                                            const total = activities.filter((activity) => {
                                                if (!activity.siteId.conducId?._id) return true;
                                                if (selectedFilterConducIds.length === 0) return true;
                                                return !selectedFilterConducIds.includes(activity.siteId.conducId?._id);
                                            }).length;
                                            return getUniqueSites(activities).map((site, index, array) => {
                                                const nbActivitiesSentInSite = sortedActivities(site._id).filter(
                                                    (activity) => activity.activityState === activityStateEnum.NEEDSSENT
                                                ).length;
                                                const nbActivitiesImportedInSite = sortedActivities(site._id).filter(
                                                    (activity) => activity.activityState === activityStateEnum.NEEDSIMPORTED
                                                ).length;
                                                const widthTotalPercent = total > 0 ? (sortedActivities(site._id).length / total) * 100 : 0;
                                                const widthSentPercent =
                                                    widthTotalPercent > 0 ? (nbActivitiesSentInSite / sortedActivities(site._id).length) * 100 : 0;
                                                const widthImportedPercent =
                                                    widthTotalPercent > 0
                                                        ? (nbActivitiesImportedInSite / sortedActivities(site._id).length) * 100
                                                        : 0;
                                                return (
                                                    <div
                                                        key={site._id}
                                                        onClick={() => {
                                                            const target = siteRefs.current[site._id];
                                                            if (target) {
                                                                target.scrollIntoView({ behavior: 'smooth', block: 'start' });
                                                            }
                                                        }}
                                                        className="fx-c"
                                                        style={{
                                                            borderTopLeftRadius:
                                                                index > 0 && array[index - 1].conducId?._id !== site.conducId?._id ? 5 : 2,
                                                            borderBottomLeftRadius:
                                                                index > 0 && array[index - 1].conducId?._id !== site.conducId?._id ? 5 : 2,
                                                            borderTopRightRadius:
                                                                index < array.length - 1 && array[index + 1].conducId?._id !== site.conducId?._id
                                                                    ? 5
                                                                    : 2,
                                                            borderBottomRightRadius:
                                                                index < array.length - 1 && array[index + 1].conducId?._id !== site.conducId?._id
                                                                    ? 5
                                                                    : 2,
                                                            width: `${widthTotalPercent}%`,

                                                            height: '100%',
                                                            opacity:
                                                                hoveredConducId &&
                                                                hoveredConducId !== site.conducId?._id &&
                                                                !selectedFilterConducIds.includes(hoveredConducId)
                                                                    ? 0.3
                                                                    : 1,
                                                            transition: 'opacity 0.2s, width 2s ease-in-out',
                                                            cursor: 'pointer',
                                                            overflow: 'hidden',
                                                            gap: 5,
                                                        }}
                                                        title={`${site.name}`}
                                                    >
                                                        <div
                                                            className="fx-fg-3 fx-r "
                                                            style={{
                                                                backgroundColor: site.conducId?.color || 'white',
                                                                borderRadius: 2,
                                                            }}
                                                        />
                                                        <div
                                                            className="fx-fg-1 fx-r "
                                                            style={{
                                                                backgroundColor: getActivityStateColor(activityStateEnum.NEEDS),
                                                                borderRadius: 2,
                                                            }}
                                                        >
                                                            <div
                                                                style={{
                                                                    width: `${widthSentPercent}%`,
                                                                    backgroundColor: getActivityStateColor(activityStateEnum.NEEDSSENT),
                                                                }}
                                                            />
                                                            <div
                                                                style={{
                                                                    width: `${widthImportedPercent}%`,
                                                                    backgroundColor: getActivityStateColor(activityStateEnum.NEEDSIMPORTED),
                                                                }}
                                                            />
                                                        </div>
                                                    </div>
                                                );
                                            });
                                        })()}
                                    </div>
                                </div>
                                <div className="fx-r gap-5px fx-w">
                                    {getConducteursTotalPrice().map((conduc) => {
                                        const total = activities.length;
                                        return (
                                            <div
                                                className="fx-c fx-fg-1 fx-fs-1 "
                                                style={{
                                                    cursor: 'pointer',
                                                    opacity:
                                                        selectedFilterConducIds.includes(conduc.conducId) ||
                                                        (hoveredConducId &&
                                                            hoveredConducId !== conduc.conducId &&
                                                            !selectedFilterConducIds.includes(hoveredConducId))
                                                            ? 0.3
                                                            : 1,
                                                    transition: 'opacity 0.2s',
                                                }}
                                                key={conduc.conducId}
                                                onMouseEnter={() => setHoveredConducId(conduc?.conducId || null)}
                                                onMouseLeave={() => setHoveredConducId(null)}
                                                onClick={() => {
                                                    if (!conduc?.conducId) return;
                                                    setSelectedFilterConducIds((prev) =>
                                                        prev.includes(conduc.conducId)
                                                            ? prev.filter((id) => id !== conduc.conducId)
                                                            : [...prev, conduc.conducId]
                                                    );
                                                }}
                                            >
                                                <div className="fx-r gap-5px fx-items-c">
                                                    <div style={{ backgroundColor: conduc?.color || '#666', height: 8, width: 8, borderRadius: 5 }} />
                                                    <div className="font-w-500">{conduc.designation}</div>
                                                    {/*<div className="text-gray">
                                                        ({total > 0 ? formatNumber((conduc.totalActivities / total) * 100, t('locale'), 0) : '?'}%)
                                                    </div>*/}
                                                </div>
                                                {/*<div className="font-w-700 lFont">
                                                    {formatNumber(conduc.totalImported, t('locale'), 0)}/
                                                    {formatNumber(conduc.totalSent, t('locale'), 0)}/
                                                    {formatNumber(conduc.totalActivities, t('locale'), 0)}
                                                </div>*/}
                                            </div>
                                        );
                                    })}
                                </div>
                            </>
                        )}
                        {isLoading && <LinearProgress sx={{ '& .MuiLinearProgress-bar': { backgroundColor: '#fff' } }} />}
                    </div>
                    <div className="fx-c gap-10px m-t-10px border-radius-10px of-y-auto h-100">
                        {getUniqueSites(activities || []).map((site) => (
                            <NeedsGlobalTable
                                key={site._id}
                                site={site}
                                activities={sortedActivities(site._id)}
                                refetch={refetch}
                                currentDate={currentDate}
                                checkEditableRow={checkEditableRow}
                                aggridRefs={aggridRefs}
                                isActivitiesTypesLoading={isActivitiesTypesLoading}
                                isUsersLoading={isUsersLoading}
                                isLoading={isLoading}
                                siteRefs={siteRefs}
                                dataActivitiesTypes={dataActivitiesTypes || []}
                                dataUsers={dataUsers || []}
                                dataTrucks={dataTrucks || []}
                            />
                        ))}
                    </div>
                </>
            )}

            {importLastNeedsModal.value && (
                <ImportLastNeedsModal
                    open={importLastNeedsModal.value}
                    handleClose={importLastNeedsModal.setFalse}
                    refetch={refetch}
                    currentDate={currentDate}
                />
            )}

            {createActivityNeedsModal.value && (
                <CreateActivityNeedsModal
                    open={createActivityNeedsModal.value}
                    handleClose={createActivityNeedsModal.setFalse}
                    refetch={refetch}
                    initDate={setSpecificHour()}
                    currentDate={currentDate}
                    stepMode={stepModeEnum.NEEDS}
                    activityState={activityStateEnum.NEEDS}
                    reportState={reportStateEnum.NEEDS}
                    activities={activities || []}
                />
            )}
        </PrimaryBox>
    );
};

export default NeedsPage;
